import React, { useState, useEffect } from 'react';
import { LogIn, Eye, EyeOff, AlertCircle, RefreshCw } from 'lucide-react';

const Login = ({ onLogin, error }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [canRetry, setCanRetry] = useState(true);
  const [attemptCount, setAttemptCount] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});
    
    try {
      setAttemptCount(prev => prev + 1);
      await onLogin(credentials);
    } catch (error) {
      // Error is handled by the parent component, but we can update local state
      console.log('Login attempt failed:', attemptCount + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });
    
    // Clear field-specific errors when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleRetry = () => {
    setCredentials({ username: '', password: '' });
    setFieldErrors({});
    setAttemptCount(0);
  };

  // Parse error message and extract field information
  useEffect(() => {
    if (error) {
      // Handle enhanced error object
      const errorMessage = typeof error === 'string' ? error : error.error || error.message || 'Login failed';
      const field = error.field;
      
      if (field === 'username') {
        setFieldErrors({ username: errorMessage });
      } else if (field === 'password') {
        setFieldErrors({ password: errorMessage });
      } else if (field === 'both') {
        setFieldErrors({ username: errorMessage, password: errorMessage });
      } else {
        setFieldErrors({});
      }
      
      setCanRetry(error.canRetry !== false);
    } else {
      setFieldErrors({});
      setCanRetry(true);
    }
  }, [error]);

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <LogIn size={32} className="login-icon" />
          <h1>Stock Analysis Dashboard</h1>
          <p>Please log in to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && !Object.keys(fieldErrors).length && (
            <div className="alert alert-error">
              <AlertCircle size={16} />
              <div className="error-content">
                <span>{typeof error === 'string' ? error : error.error || error.message || 'Login failed'}</span>
                {canRetry && attemptCount > 0 && (
                  <div className="error-actions">
                    <button
                      type="button"
                      onClick={handleRetry}
                      className="retry-button"
                    >
                      <RefreshCw size={14} />
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={credentials.username}
              onChange={handleChange}
              required
              autoComplete="username"
              autoFocus
              className={fieldErrors.username ? 'error' : ''}
              placeholder="Enter your username"
            />
            {fieldErrors.username && (
              <div className="field-error">
                <AlertCircle size={14} />
                <span>{fieldErrors.username}</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                className={fieldErrors.password ? 'error' : ''}
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {fieldErrors.password && (
              <div className="field-error">
                <AlertCircle size={14} />
                <span>{fieldErrors.password}</span>
              </div>
            )}
          </div>

          {attemptCount > 0 && (
            <div className="login-info">
              <span>Attempt {attemptCount}</span>
              {attemptCount > 2 && (
                <span className="hint">
                  Default credentials: admin / password123
                </span>
              )}
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={loading || !credentials.username || !credentials.password}
          >
            {loading ? (
              <>
                <RefreshCw size={16} className="spinning" />
                Logging in...
              </>
            ) : (
              <>
                <LogIn size={16} />
                {attemptCount > 0 ? 'Try Again' : 'Log In'}
              </>
            )}
          </button>
        </form>

      </div>
    </div>
  );
};

export default Login;